/* eslint-disable no-console */
/**
 * Tab COMPRAS — tabla de documentos de costo (Fase 3b).
 *
 * Una fila por DOCUMENTO, de las tres fuentes unificadas por `documentosCosto.js`: facturas
 * SII recibidas (afectas / exentas / NC), facturas externas y boletas de honorarios.
 *
 * DOS EJES INDEPENDIENTES, uno por chip, y cada chip es también su filtro:
 *   · Clasificación (ER)  — Sin clasificar / [Cuenta] / Activo / Ignorada
 *   · Pago (partida abierta) — Impaga / Parcial X% / Pagada / Cubierta a cuenta / No aplica
 *
 * Ninguno de los dos sale de `estado_vinculacion` (regla 10.65): ese flag miente en el 21,8%
 * de los documentos de 2026. Ver el header de `documentosCosto.js`.
 *
 * Es una vista de LECTURA. La edición sigue yendo a los modales que ya existen — no se
 * reimplementa ninguno.
 */
import { useState, useMemo } from "react";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import {
  CLASIFICACION,
  PAGO,
  TIPO_DOCUMENTO,
  ORIGEN,
  ESTADO_SII,
  esPasivoFirme,
} from "../../utils/compras/documentosCosto";
import { useDocumentosCosto } from "../../utils/compras/useDocumentosCosto";
import AsignarRecibidaModal from "../ConciliacionSII/AsignarRecibidaModal";
import ClasificarHonorarioModal from "./ClasificarHonorarioModal";
import ClasificarModal, { ALCANCE } from "./ClasificarModal";
import styles from "./VentasFacturas.module.scss";

// ─── Helpers de formato ────────────────────────────────────

const formatMonto = (monto, moneda) => {
  if (monto == null) return "-";
  const n = Number(monto);
  if (moneda === "USD") return `US$ ${n.toLocaleString("es-CL", { maximumFractionDigits: 2 })}`;
  return `$ ${n.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  const [, month, day] = fechaStr.substring(0, 10).split("-");
  return `${day}/${month}`;
};

const formatRut = (rut) => {
  if (!rut) return null;
  const [cuerpo, dv] = rut.split("-");
  if (!dv) return rut;
  return `${Number(cuerpo).toLocaleString("es-CL")}-${dv}`;
};

// Formas jurídicas y siglas chilenas: se conservan con su casing canónico en vez del Title
// Case ingenuo (que daría "Spa"/"Ltda"). Clave = token en MAYÚSCULA; valor = forma correcta.
const SIGLAS = {
  SPA: "SpA",
  "S.P.A": "SpA",
  "S.P.A.": "SpA",
  LTDA: "Ltda",
  "LTDA.": "Ltda",
  SA: "SA",
  "S.A": "S.A.",
  "S.A.": "S.A.",
  EIRL: "EIRL",
  "E.I.R.L": "E.I.R.L.",
  "E.I.R.L.": "E.I.R.L.",
};

// Nombre del proveedor a Title Case, SOLO para display en la tabla (los datos en DB no se
// tocan). Cada palabra: 1ª mayúscula, resto minúscula. Excepciones: las siglas del mapa
// (SpA/Ltda/S.A./…) y cualquier token con punto interno (iniciales tipo "S.A.") se dejan
// como corresponde en vez de "Spa". Limitación: los acrónimos SIN punto (MMS, HDI, PCS) no
// se distinguen de una palabra corta común y caen en Title Case → "Mms", "Hdi".
const formatProveedor = (nombre) => {
  if (!nombre || nombre === "—") return nombre || "—";
  return nombre
    .trim()
    .split(/\s+/)
    .map((token) => {
      const up = token.toUpperCase();
      if (SIGLAS[up]) return SIGLAS[up];
      // Iniciales con punto (no listadas): se preservan en mayúscula.
      if (token.includes(".")) return up;
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(" ");
};

// ─── Chips ─────────────────────────────────────────────────
// Mismo formato de pastilla que ya usaba la tabla (padding 2/8, radio 10, 0.7rem, 600).

const chipStyle = (bg, color) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 10,
  fontSize: "0.7rem",
  fontWeight: 600,
  background: bg,
  color,
  whiteSpace: "nowrap",
});

// Chip de la columna Tipo: ancho uniforme para que la columna lea como una pila alineada
// (el más largo es "Nota Crédito"). Centrado, no envuelve.
const tipoChip = (bg, color) => ({ ...chipStyle(bg, color), minWidth: 92, textAlign: "center" });

const CHIP_TIPO = {
  [TIPO_DOCUMENTO.FACTURA]: tipoChip("#eaf2f8", "#2874a6"),
  [TIPO_DOCUMENTO.EXENTA]: tipoChip("#f4ecf7", "#7d3c98"),
  // La NC en rojo: es el único tipo que RESTA.
  [TIPO_DOCUMENTO.NOTA_CREDITO]: tipoChip("#fadbd8", "#a93226"),
  [TIPO_DOCUMENTO.HONORARIO]: tipoChip("#fef9e7", "#9a7d0a"),
  // Externa: antes gris sobre gris (bajo contraste). Slate más oscuro sobre gris claro.
  [TIPO_DOCUMENTO.EXTERNA]: tipoChip("#d6dbdf", "#2c3e50"),
};

// Label de display del chip. `TIPO_DOCUMENTO.NOTA_CREDITO` es "NC" (clave de datos/filtros),
// pero en la celda se muestra completo. El resto coincide con su value.
const LABEL_TIPO = { [TIPO_DOCUMENTO.NOTA_CREDITO]: "Nota Crédito" };

const CHIP_CLASIFICACION = {
  [CLASIFICACION.SIN_CLASIFICAR]: chipStyle("#fdebd0", "#a66300"),
  [CLASIFICACION.CUENTA]: chipStyle("#d5f5e3", "#1e8449"),
  [CLASIFICACION.ACTIVO]: chipStyle("#d6eaf8", "#2874a6"),
  [CLASIFICACION.IGNORADA]: chipStyle("#e5e7e9", "#707b7c"),
};

const CHIP_PAGO = {
  [PAGO.IMPAGA]: chipStyle("#fadbd8", "#a93226"),
  [PAGO.PARCIAL]: chipStyle("#fdebd0", "#a66300"),
  [PAGO.PAGADA]: chipStyle("#d5f5e3", "#1e8449"),
  // Violeta: hay plata, falta la formalización del vínculo. Antes era turquesa (muy parecido
  // al verde de "Pagada" y se confundían); el violeta la separa claramente.
  [PAGO.CUBIERTA]: chipStyle("#e8daef", "#6c3483"),
  [PAGO.NO_APLICA]: chipStyle("#f4f6f6", "#909497"),
};

const TITULO_PAGO = {
  [PAGO.PAGADA]: "Pagada y formalizada: hay vínculo a movimiento bancario",
  [PAGO.PARCIAL]: "Vínculo bancario que no cubre el total del documento",
  [PAGO.CUBIERTA]: "El ledger la da por saldada con pagos a cuenta, SIN vínculo formal",
  [PAGO.IMPAGA]: "Sin vínculo bancario y con saldo vivo en el ledger",
  [PAGO.NO_APLICA]: "Una nota de crédito no se paga: reduce deuda",
};

// Eje estado_sii (ciclo de vida RCV). Aceptada en verde sobrio (estado normal, no distrae);
// Pendiente en ámbar (visible pero no es deuda firme); Rechazada en gris-rojo apagado.
const CHIP_ESTADO_SII = {
  [ESTADO_SII.ACEPTADA]: chipStyle("#eafaf1", "#1e8449"),
  [ESTADO_SII.PENDIENTE]: chipStyle("#fdebd0", "#a66300"),
  [ESTADO_SII.RECHAZADA]: chipStyle("#eceff1", "#78909c"),
};

const LABEL_ESTADO_SII = {
  [ESTADO_SII.ACEPTADA]: "Aceptada",
  // Chip corto; el detalle "pendiente por aprobar" vive en el tooltip (TITULO_ESTADO_SII).
  [ESTADO_SII.PENDIENTE]: "Pendiente",
  [ESTADO_SII.RECHAZADA]: "Rechazada",
};

const TITULO_ESTADO_SII = {
  [ESTADO_SII.ACEPTADA]: "En el RCV del SII — pasivo firme",
  [ESTADO_SII.PENDIENTE]: "En obligations/pending: aún sin acuse de recibo (Ley 19.983). NO suma al impago firme",
  [ESTADO_SII.RECHAZADA]: "Rechazada en el SII — no es deuda",
};

// El chip muestra solo la palabra del estado; el detalle (% amortizado, cuotas) vive en este
// tooltip para no perderlo. El detalle completo irá al modal de pagos en Fase 4.
const tooltipPago = (pago) => {
  const partes = [TITULO_PAGO[pago.estado]];
  if (pago.estado === PAGO.PARCIAL && pago.porcentaje) partes.push(`${pago.porcentaje}% amortizado`);
  if (pago.plan?.tieneCuotas) partes.push(`${pago.plan.cuotasPagadas}/${pago.plan.cuotas} cuotas`);
  return partes.filter(Boolean).join(" · ");
};

// El chip de Clasificación dispara el override per-factura SOLO para SII recibidas no-NC y no
// ignoradas: la NC (61) no se clasifica y la ignorada es un descarte contable, no una cuenta.
const esOverrideable = (d) =>
  d.origen === ORIGEN.SII &&
  d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO &&
  d.clasificacion.estado !== CLASIFICACION.IGNORADA;

// ─── Componente ────────────────────────────────────────────

const ComprasTab = ({ pais, filtros = {}, reloadNonce = 0 }) => {
  const { meses = [], clasificacion = "", tipo = "", pago = "", estadoSii = "", busqueda = "" } = filtros;
  const [modalFactura, setModalFactura] = useState(null);
  const [modalHonorario, setModalHonorario] = useState(null);
  // Modal ÚNICO de clasificación: { doc, alcance }. La FILA lo abre con alcance proveedor;
  // el CHIP, con alcance factura. Mismo componente, selector de alcance adentro.
  const [modalClasificar, setModalClasificar] = useState(null);
  const { documentos, cargando, recargar } = useDocumentosCosto(pais);

  // `reloadNonce` lo mueve el botón "Sincronizar SII" del padre.
  const [ultimoNonce, setUltimoNonce] = useState(reloadNonce);
  if (reloadNonce !== ultimoNonce) {
    setUltimoNonce(reloadNonce);
    recargar();
  }

  const visibles = useMemo(() => {
    let arr = documentos;
    if (meses.length > 0) arr = arr.filter((d) => d.fecha && meses.includes(d.fecha.substring(0, 7)));
    if (tipo) arr = arr.filter((d) => d.tipo === tipo);
    if (pago) arr = arr.filter((d) => d.pago.estado === pago);
    if (estadoSii) arr = arr.filter((d) => d.estadoSii === estadoSii);
    if (clasificacion === "__sin__") arr = arr.filter((d) => d.clasificacion.estado === CLASIFICACION.SIN_CLASIFICAR);
    else if (clasificacion === "__activo__") arr = arr.filter((d) => d.clasificacion.estado === CLASIFICACION.ACTIVO);
    else if (clasificacion === "__ignorada__")
      arr = arr.filter((d) => d.clasificacion.estado === CLASIFICACION.IGNORADA);
    else if (clasificacion) arr = arr.filter((d) => d.clasificacion.cuenta === clasificacion);
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      arr = arr.filter(
        (d) =>
          (d.proveedorNombre || "").toLowerCase().includes(q) ||
          (d.proveedorRut || "").toLowerCase().includes(q) ||
          (d.folio || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [documentos, meses, tipo, pago, clasificacion, estadoSii, busqueda]);

  // Las cards miden el PERÍODO seleccionado, no el filtro de chips: son el titular de qué
  // falta hacer, y filtrar por "Sin clasificar" no puede hacer que la card diga otra cosa.
  const resumen = useMemo(() => {
    const delPeriodo =
      meses.length > 0 ? documentos.filter((d) => d.fecha && meses.includes(d.fecha.substring(0, 7))) : documentos;
    const sinNC = delPeriodo.filter((d) => d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO);
    const sinClasificar = sinNC.filter((d) => d.clasificacion.estado === CLASIFICACION.SIN_CLASIFICAR);
    // IMPAGO FIRME: solo documentos aceptados (en el RCV). Las 'pendiente por aprobar' y
    // 'rechazada' NO son deuda firme (regla 10.154) — van a su propio bucket, no acá.
    const impagas = sinNC.filter(
      (d) => esPasivoFirme(d) && (d.pago.estado === PAGO.IMPAGA || d.pago.estado === PAGO.PARCIAL)
    );
    // Bucket aparte: compras que el SII aún no tiene acusadas. Se ven como grupo, NO suman al impago.
    const pendientesAprob = sinNC.filter((d) => d.estadoSii === ESTADO_SII.PENDIENTE);
    return {
      sinClasificar: { n: sinClasificar.length, monto: sinClasificar.reduce((s, d) => s + d.total, 0) },
      impago: { n: impagas.length, monto: impagas.reduce((s, d) => s + (d.pago.saldo || d.total), 0) },
      pendientesAprob: {
        n: pendientesAprob.length,
        monto: pendientesAprob.reduce((s, d) => s + d.total, 0),
      },
      // IVA crédito: la NC RESTA (regla 10.42). Solo SII — las externas no generan crédito.
      iva: delPeriodo.filter((d) => d.origen === ORIGEN.SII).reduce((s, d) => s + d.signo * d.iva, 0),
      // La retención NO es costo: es lo que la empresa entera al SII por las BHE.
      retencion: delPeriodo.filter((d) => d.origen === ORIGEN.HONORARIO).reduce((s, d) => s + (d.retencion || 0), 0),
    };
  }, [documentos, meses]);

  const abrirEdicion = (doc) => {
    if (doc.origen === ORIGEN.SII) {
      // Con proveedor identificado → modal de clasificación con alcance PROVEEDOR por default
      // (propaga a sus recibidas 2026 sin clasificar; se puede cambiar a "solo esta factura").
      // Sin proveedor → AsignarRecibidaModal, que primero resuelve el proveedor.
      if (doc.proveedorId) setModalClasificar({ doc, alcance: ALCANCE.PROVEEDOR });
      else setModalFactura(doc);
    } else if (doc.origen === ORIGEN.HONORARIO) {
      setModalHonorario(doc);
    }
    // Externas todavía se clasifican desde EstadoCuenta (Fase C).
  };

  const cards = [
    {
      label: `SIN CLASIFICAR (${resumen.sinClasificar.n})`,
      valor: formatMonto(resumen.sinClasificar.monto),
      extra: resumen.sinClasificar.n > 0 ? styles.cardRed : "",
    },
    {
      label: `IMPAGO (${resumen.impago.n})`,
      valor: formatMonto(resumen.impago.monto),
      extra: resumen.impago.n > 0 ? styles.cardRed : "",
    },
    {
      // Bucket propio: compras que el SII aún no tiene acusadas. NO suman al impago firme.
      label: `PEND. APROBAR (${resumen.pendientesAprob.n})`,
      valor: formatMonto(resumen.pendientesAprob.monto),
      extra: resumen.pendientesAprob.n > 0 ? styles.cardAmber : "",
    },
    { label: "IVA CRÉDITO", valor: formatMonto(resumen.iva), extra: styles.cardBlue },
    { label: "RETENCIÓN HON.", valor: formatMonto(resumen.retencion), extra: "" },
  ];

  return (
    <div className={styles.containerFacturasDetalle} style={{ paddingTop: 0 }}>
      <div className={styles.cardsSectionCompras}>
        {cards.map((c) => (
          <div key={c.label} className={`${styles.card} ${c.extra}`}>
            <div className={styles.cardLabel}>{c.label}</div>
            <div style={{ marginTop: 5 }}>
              <span className={styles.cardValue}>{c.valor}</span>
            </div>
          </div>
        ))}
      </div>

      {cargando ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>Cargando...</div>
      ) : visibles.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>No hay documentos con estos filtros</div>
      ) : (
        <div className={styles.tableResponsive}>
          <table className={styles.table}>
            <thead className={styles.stickyHead}>
              <tr>
                <th className={styles.colCmpFecha}>Fecha</th>
                <th className={styles.colCmpTipoDte} style={{ textAlign: "left" }}>
                  Tipo
                </th>
                <th className={styles.colCmpProveedor} style={{ textAlign: "left" }}>
                  Proveedor
                </th>
                <th className={styles.colCmpRut}>RUT</th>
                <th className={styles.colCmpFolio}>Folio</th>
                <th className={styles.colCmpNeto} style={{ textAlign: "right" }}>
                  Neto
                </th>
                <th className={styles.colCmpIva} style={{ textAlign: "right" }}>
                  IVA
                </th>
                <th className={styles.colCmpTotal} style={{ textAlign: "right" }}>
                  Total
                </th>
                <th className={styles.colCmpEstado} style={{ textAlign: "left" }}>
                  Estado SII
                </th>
                <th className={styles.colCmpEstado} style={{ textAlign: "left" }}>
                  Clasificación ER
                </th>
                <th className={styles.colCmpEstado} style={{ textAlign: "left" }}>
                  Pago
                </th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((d) => {
                const editable = d.origen === ORIGEN.SII || d.origen === ORIGEN.HONORARIO;
                // Neto: si es 0 (exentas 34, honorarios) se muestra el exento — es el costo
                // real del documento. Mismo criterio que la tabla original.
                const netoMostrar = d.neto > 0 ? d.neto : d.exento;
                return (
                  <tr
                    key={d.clave}
                    onClick={() => abrirEdicion(d)}
                    style={{ cursor: editable ? "pointer" : "default" }}
                  >
                    <td className={styles.colCmpFecha} style={{ textAlign: "center" }}>
                      {formatFecha(d.fecha)}
                    </td>
                    <td className={styles.colCmpTipoDte}>
                      <span style={CHIP_TIPO[d.tipo]}>{LABEL_TIPO[d.tipo] || d.tipo}</span>
                    </td>
                    <td
                      className={styles.colCmpProveedor}
                      title={
                        d.razonSocial && d.razonSocial !== d.proveedorNombre
                          ? `Razón social SII: ${d.razonSocial}`
                          : undefined
                      }
                    >
                      {formatProveedor(d.proveedorNombre)}
                    </td>
                    <td className={styles.colCmpRut} style={{ textAlign: "center" }}>
                      {formatRut(d.proveedorRut) || "—"}
                    </td>
                    <td className={styles.colCmpFolio} style={{ textAlign: "center" }}>
                      {d.folio || "—"}
                    </td>
                    <td className={styles.colCmpNeto} style={{ textAlign: "right" }}>
                      {formatMonto(netoMostrar, d.moneda)}
                    </td>
                    <td className={styles.colCmpIva} style={{ textAlign: "right" }}>
                      {formatMonto(d.iva, d.moneda)}
                    </td>
                    <td className={styles.colCmpTotal} style={{ textAlign: "right", fontWeight: 600 }}>
                      {d.signo < 0 ? `− ${formatMonto(d.total, d.moneda)}` : formatMonto(d.total, d.moneda)}
                    </td>
                    <td className={styles.colCmpEstado}>
                      {/* Ciclo de vida SII (RCV). Solo aplica a documentos SII; externas/honorarios no. */}
                      {d.origen === ORIGEN.SII ? (
                        <span style={CHIP_ESTADO_SII[d.estadoSii]} title={TITULO_ESTADO_SII[d.estadoSii]}>
                          {LABEL_ESTADO_SII[d.estadoSii]}
                        </span>
                      ) : (
                        <span style={{ color: "#bbb" }}>—</span>
                      )}
                    </td>
                    <td className={styles.colCmpEstado}>
                      {esOverrideable(d) ? (
                        // Clic en el chip = abrir el modal con alcance "solo esta factura" por
                        // default. stopPropagation para no disparar también el onClick de la fila
                        // (que lo abre con alcance proveedor).
                        <span
                          style={{ ...CHIP_CLASIFICACION[d.clasificacion.estado], cursor: "pointer" }}
                          title="Clasificar (esta factura o todo el proveedor)"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModalClasificar({ doc: d, alcance: ALCANCE.FACTURA });
                          }}
                        >
                          {d.clasificacion.label}
                        </span>
                      ) : (
                        <span style={CHIP_CLASIFICACION[d.clasificacion.estado]}>{d.clasificacion.label}</span>
                      )}
                    </td>
                    <td className={styles.colCmpEstado}>
                      <span style={CHIP_PAGO[d.pago.estado]} title={tooltipPago(d.pago)}>
                        {d.pago.estado}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalFactura && (
        <AsignarRecibidaModal
          isOpen
          toggle={() => setModalFactura(null)}
          factura={modalFactura.raw}
          onAsignada={() => {
            setModalFactura(null);
            recargar();
          }}
        />
      )}

      {modalHonorario && (
        <ClasificarHonorarioModal
          boleta={modalHonorario.raw}
          onClose={() => setModalHonorario(null)}
          onClasificada={() => {
            setModalHonorario(null);
            recargar();
          }}
        />
      )}

      {modalClasificar && (
        <ClasificarModal
          doc={modalClasificar.doc}
          alcanceInicial={modalClasificar.alcance}
          onClose={() => setModalClasificar(null)}
          onAplicada={() => {
            setModalClasificar(null);
            recargar();
          }}
        />
      )}
    </div>
  );
};

/** Opciones del filtro de Clasificación — el mismo eje que el chip. */
export const OPCIONES_CLASIFICACION = [
  { value: "", label: "Toda clasificación" },
  { value: "__sin__", label: "Sin clasificar" },
  { value: "__activo__", label: "Activo" },
  { value: "__ignorada__", label: "Ignorada" },
];

/** Opciones del filtro de Pago — el mismo eje que el chip. */
export const OPCIONES_PAGO = [
  { value: "", label: "Todo estado de pago" },
  { value: PAGO.IMPAGA, label: "Impaga" },
  { value: PAGO.PARCIAL, label: "Parcial" },
  { value: PAGO.PAGADA, label: "Pagada (formal)" },
  { value: PAGO.CUBIERTA, label: "Cubierta a cuenta" },
  { value: PAGO.NO_APLICA, label: "No aplica (NC)" },
];

/** Opciones del filtro de Estado SII (ciclo de vida RCV) — mismo eje que el chip "Estado". */
export const OPCIONES_ESTADO_SII = [
  { value: "", label: "Todo estado SII" },
  { value: ESTADO_SII.ACEPTADA, label: "Aceptada" },
  { value: ESTADO_SII.PENDIENTE, label: "Pendiente por aprobar" },
  { value: ESTADO_SII.RECHAZADA, label: "Rechazada" },
];

/** Opciones del filtro de Tipo — la NC aislada, que es el pedido explícito. */
export const OPCIONES_TIPO = [
  { value: "", label: "Todo tipo" },
  { value: TIPO_DOCUMENTO.FACTURA, label: "Factura afecta" },
  { value: TIPO_DOCUMENTO.EXENTA, label: "Exenta" },
  { value: TIPO_DOCUMENTO.NOTA_CREDITO, label: "Nota de crédito" },
  { value: TIPO_DOCUMENTO.HONORARIO, label: "Honorario" },
  { value: TIPO_DOCUMENTO.EXTERNA, label: "Externa" },
];

export const CUENTAS_AGRUPADAS = [...new Set(CUENTAS_ER.map((c) => c.grupo))].map((grupo) => ({
  grupo,
  cuentas: CUENTAS_ER.filter((c) => c.grupo === grupo),
}));

export default ComprasTab;
